import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import questionIcon from "../assets/images/question.svg";
import answerIcon from "../assets/images/answer.svg";
import viewDocumentIcon from "../assets/images/viewDocument.svg";
import saveIcon from "../assets/images/Save.svg";
import copyIcon from "../assets/images/copy.svg";
import likeIcon from "../assets/images/like.svg";
import dislikeIcon from "../assets/images/dislike.svg";
import shareIcon from "../assets/images/share.svg";
import CommonValues from "../Common/utils";

interface ChatMessage {
  question: string;
  answer: string;
  documents?: string[];
  chatId?: string;
  isBookmarked?: boolean;
  followUpQuestions?: string[];
  sources?: string[];
  conversationId?: string;
  isStreaming?: boolean;
}

interface QuestionAnswerProps {
  messages: ChatMessage[];
  onOpenDocumentClick: () => void;
  onFollowUpQuestionClick?: (question: string) => void;
  category ?: string;
}

const CopyButton = ({ text }: { text: string }) => {
  const [showCopied, setShowCopied] = useState(false);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <div className="position-relative">
      <button
        className="btn btn-link p-0 text-muted"
        title="Copy"
        onClick={() => copyToClipboard(text)}
      >
        <img src={copyIcon} alt="Copy" className="action-icon" />
      </button>
      <div
        className="copy-notification"
        style={{
          position: "absolute",
          bottom: "100%",
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "#333",
          color: "white",
          padding: "4px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          opacity: showCopied ? 1 : 0,
          visibility: showCopied ? "visible" : "hidden",
          transition: "opacity 0.2s ease-in-out, visibility 0.2s ease-in-out",
          whiteSpace: "nowrap",
          marginBottom: "8px",
          zIndex: 1000,
        }}
      >
        Copied to clipboard!
      </div>
    </div>
  );
};

const preprocessMarkdown = (content: string): string => {
  if (!content) return "";

  const followUpMatch = content.match(
    /\$\$FollowupQuestions\$\$([\s\S]*?)\$\$FollowupQuestionsEND\$\$/
  );
  if (followUpMatch) {
    const followUpSection = followUpMatch[1].trim();
    const questions = followUpSection.split("\n").filter((q) => q.trim());
    content = content.replace(followUpMatch[0], "").trim();
  }

  content = content.replace(/\$\$PARENTDOMIDSTART\$\$[\s\S]*$/, "");

  return content
    .replace(/^(\d+)\.\s*\n/gm, "$1. ")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^\s*[-*]\s*\n/gm, "- ")
    .replace(/(\d+\.|\*|-)\s*\n\s*([^\s])/g, "$1 $2")
    .replace(/^\s+/gm, "")
    .replace(/(#{1,6}\s.*)\n{2,}/g, "$1\n\n")
    .trim();
};

const extractFollowUpQuestions = (content?: string): string[] => {
  if (!content) return [];
  const followUpMatch = content.match(
    /\$\$FollowupQuestions\$\$([\s\S]*?)\$\$FollowupQuestionsEND\$\$/
  );
  if (followUpMatch) {
    return followUpMatch[1]
      .trim()
      .split("\n")
      .filter((q) => q.trim());
  }
  return [];
};

const ThreeLinesSkeleton = () => (
  <div className="skeleton-loader">
    <div className="skeleton-line"></div>
    <div className="skeleton-line"></div>
    <div className="skeleton-line"></div>
  </div>
);

export default function QuestionAnswer({
  messages,
  onOpenDocumentClick,
  onFollowUpQuestionClick,
  category,
}: QuestionAnswerProps) {
  const [feedbackStates, setFeedbackStates] = useState<{
    [key: string]: { like: boolean; dislike: boolean };
  }>({});
  const [isSubmitting, setIsSubmitting] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [bookmarkLoading, setBookmarkLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [bookmarkStates, setBookmarkStates] = useState<{
    [key: string]: boolean;
  }>({});
  const navigate = useNavigate();

  const containerRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<(HTMLDivElement | null)[]>([]);

  const isAtBottomRef = useRef(true);
  const prevLastKeyRef = useRef<string | null>(null);
  const scrollTimeoutRef = useRef<number | null>(null);
  const BOTTOM_THRESHOLD = 48;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onScroll = () => {
      const atBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD;
      isAtBottomRef.current = atBottom;
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToBottom = (smooth: boolean) => {
    const el = containerRef.current;
    if (!el) return;
    const top = el.scrollHeight;
    el.scrollTo({ top, behavior: smooth ? "smooth" : "auto" });
  };

  const getIsStreaming = (m?: ChatMessage): boolean => {
    if (!m) return false;
    const ans = m.answer ?? "";
    return ans === "" || ans === "Loading...";
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el || messages.length === 0) return;

    const last = messages[messages.length - 1];
    const isStreaming = getIsStreaming(last);
    const lastKey = last?.chatId ?? `idx:${messages.length - 1}`;
    if (!isAtBottomRef.current) {
      prevLastKeyRef.current = lastKey;
      return;
    }

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }

    scrollTimeoutRef.current = window.setTimeout(
      () => {
        scrollToBottom(!isStreaming);
        prevLastKeyRef.current = lastKey;
      },
      isStreaming ? 50 : 0
    );

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
  }, [messages]);

  const scrollToTop = () => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = 0;
    isAtBottomRef.current = false;
  };

  const handleFeedback = async (messageIndex: number, isLike: boolean) => {
    const message = messages[messageIndex];
    if (!message.chatId) {
      console.warn("No chatId found for message:", message);
      return;
    }

    setIsSubmitting((prev) => ({ ...prev, [messageIndex]: true }));

    const newFeedbackState = {
      like: isLike,
      dislike: !isLike,
    };

    try {
      const token = localStorage.getItem("igtoken");
      if (!token) {
        throw new Error("No authorization token found");
      }

      await axios.put(
        `${process.env.REACT_APP_NCSupportBot_API}/api/ncsupportbot/nc-support-bot-reaction`,
        {
          ChatId: message.chatId,
          Like: isLike,
          DisLike: !isLike,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setFeedbackStates((prev) => ({
        ...prev,
        [messageIndex]: newFeedbackState,
      }));
    } catch (error: any) {
      if (error?.response?.status === 401) {
        navigate("/");
        CommonValues.Logout(navigate);
      }
      console.error("Error sending feedback:", error);
    } finally {
      setIsSubmitting((prev) => ({ ...prev, [messageIndex]: false }));
    }
  };

  const handleBookmark = async (messageIndex: number) => {
    const message = messages[messageIndex];
    if (!message.chatId) {
      console.warn("No chatId found for message:", message);
      return;
    }
    setBookmarkLoading((prev) => ({ ...prev, [messageIndex]: true }));
    try {
      const token = localStorage.getItem("igtoken");
      if (!token) {
        throw new Error("No authorization token found");
      }

      await axios.post(
        `${process.env.REACT_APP_Alexandria_API}/api/Alexandria/bookmark/add`,
        {
          conversationId: message.conversationId,
          bookmarkedTime: new Date().toISOString(),
          title: message.question,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setBookmarkStates((prev) => ({
        ...prev,
        [messageIndex]: true,
      }));
    } catch (error: any) {
      if (error?.response?.status === 401) {
        navigate("/");
        CommonValues.Logout(navigate);
      }
      console.error("Error bookmarking chat:", error);
    } finally {
      setBookmarkLoading((prev) => ({ ...prev, [messageIndex]: false }));
    }
  };

  const location = useLocation();
  const fromHistory = location.state?.fromHistory || false;
  const lastLoggedConversationId = useRef<string | null>(null);

  useEffect(() => {
    if (!fromHistory && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      const { conversationId, question } = lastMessage;

      if (
        conversationId &&
        question &&
        lastLoggedConversationId.current !== conversationId
      ) {
        addChatHistory(conversationId, question, category);
        lastLoggedConversationId.current = conversationId;
      }
    }
  }, [messages, category]);

  const addChatHistory = async (
    conversationId: string,
    question: string,
    category?: string
  ) => {
    try {
      const token = localStorage.getItem("igtoken");
      await axios.post(
        `${process.env.REACT_APP_Alexandria_API}/api/Alexandria/chathistory/add`,
        // `https://localhost:58677/api/Alexandria/chathistory/add`,
        {
          conversationId: conversationId,
          question: question,
          chatTime: new Date().toISOString(),
          category: category || "support",
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log("Chat history saved successfully");
    } catch (error) {
      console.error("Failed to save chat history:", error);
    }
  };

  const handleFollowUpQuestionClick = (question: string) => {
    if (onFollowUpQuestionClick) {
      onFollowUpQuestionClick(question);
    }
    setTimeout(() => {
      scrollToTop();
    }, 50);
  };

  return (
    <div className="container-2xl">
      <div
        ref={containerRef}
        className="bg-white border rounded mt-0 mx-0 question-answer-block p-4 text-14 chat-container"
        style={{ maxHeight: "90vh", overflowY: "auto" }}
      >
        {messages.map((message, index) => {
          const followUpQuestions = extractFollowUpQuestions(message.answer);
          const processedAnswer = preprocessMarkdown(message.answer);
          const isLastMessage = index === messages.length - 1;
          const isStreaming =
            isLastMessage &&
            (message.answer === "" || processedAnswer === "Loading...");
          const MIN_STREAM_HEIGHT = 64;

          const showLoader =
            (isLastMessage && message.answer === "") ||
            processedAnswer === "Loading...";

          return (
            <div
              key={index}
              ref={(el) => {
                messageRefs.current[index] = el;
              }}
              style={{ minHeight: "auto" }}
            >
              <div className="d-flex align-items-center mb-4 bg-light rounded p-3 ">
                <div className="me-3">
                  <img src={questionIcon} alt="Question" className="me-2" />
                </div>
                <div className="flex-grow-1">
                  <span className="fw-medium">{message.question}</span>
                </div>
              </div>

              <div className="d-flex align-items-start mb-4 p-3">
                <div className="me-3">
                  <img src={answerIcon} alt="Answer" className="me-2" />
                </div>
                <div className="flex-grow-1">
                  <div className="mb-4">
                    {showLoader ? (
                      <div
                        style={{
                          minHeight: `${MIN_STREAM_HEIGHT}px`,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <ThreeLinesSkeleton />
                      </div>
                    ) : (
                      <div
                        className="answer-text markdown-content"
                        style={{
                          whiteSpace: "pre-wrap",
                          lineHeight: "1.2",
                          fontSize: "0.95rem",
                          wordBreak: "break-word",
                          minHeight: isStreaming
                            ? `${MIN_STREAM_HEIGHT}px`
                            : undefined,
                        }}
                      >
                        <ReactMarkdown
                          components={{
                            a: ({ node, ...props }) => (
                              <a
                                {...props}
                                className="text-primary text-decoration-none"
                                target="_blank"
                                rel="noopener noreferrer"
                              />
                            ),
                            p: ({ node, ...props }) => (
                              <p
                                {...props}
                                style={{
                                  marginTop: "0rem",
                                  marginBottom: "0.2rem",
                                  lineHeight: "1.3rem",
                                }}
                              />
                            ),
                            ul: ({ node, ...props }) => (
                              <ul
                                {...props}
                                className="ps-4 mb-2"
                                style={{ lineHeight: "0.9rem" }}
                              />
                            ),
                            ol: ({ node, ...props }) => (
                              <ol
                                {...props}
                                className="mb-0 ps-1"
                                style={{ marginBottom: "0", lineHeight: "1" }}
                              />
                            ),
                            li: ({ node, ...props }) => (
                              <li
                                {...props}
                                className="mb-0"
                                style={{
                                  display: "list-item",
                                  lineHeight: "1.3rem",
                                }}
                              />
                            ),
                            code: (props: {
                              inline?: boolean;
                              className?: string;
                              [key: string]: any;
                            }) => {
                              const { inline, ...rest } = props;
                              return inline ? (
                                <code
                                  {...rest}
                                  className="bg-light px-1 py-0.5 rounded"
                                  style={{ fontSize: "0.9em" }}
                                />
                              ) : (
                                <code
                                  {...rest}
                                  className="d-block bg-light p-2 rounded mb-2"
                                  style={{
                                    fontSize: "0.9em",
                                    overflowX: "auto",
                                  }}
                                />
                              );
                            },
                            pre: ({ node, ...props }) => (
                              <pre
                                {...props}
                                className="bg-light p-2 rounded mb-2"
                                style={{ fontSize: "0.9em", overflowX: "auto" }}
                              />
                            ),
                            blockquote: ({ node, ...props }) => (
                              <blockquote
                                {...props}
                                className="border-start border-3 ps-3 py-1 my-2"
                                style={{
                                  fontSize: "0.95em",
                                  marginBottom: "0.3rem",
                                }}
                              />
                            ),
                            h1: ({ node, ...props }) => (
                              <h1
                                {...props}
                                className="h4 mb-1"
                                style={{ marginBottom: "1rem" }}
                              />
                            ),
                            h2: ({ node, ...props }) => (
                              <h2
                                {...props}
                                className="h5 mb-1"
                                style={{ marginBottom: "1rem" }}
                              />
                            ),
                            h3: ({ node, ...props }) => (
                              <h3
                                {...props}
                                className="h6 mb-1"
                                style={{ marginBottom: "1rem" }}
                              />
                            ),
                            h4: ({ node, ...props }) => (
                              <h4
                                {...props}
                                className="h6 mb-1"
                                style={{ marginBottom: "1rem" }}
                              />
                            ),
                            h5: ({ node, ...props }) => (
                              <h5
                                {...props}
                                className="h6 mb-1"
                                style={{ marginBottom: "1rem" }}
                              />
                            ),
                            h6: ({ node, ...props }) => (
                              <h6
                                {...props}
                                className="h6 mb-1"
                                style={{ marginBottom: "1rem" }}
                              />
                            ),
                            table: ({ node, ...props }) => (
                              <div className="table-responsive">
                                <table
                                  {...props}
                                  className="table table-bordered mb-2"
                                  style={{
                                    fontSize: "0.9em",
                                    width: "100%",
                                    tableLayout: "auto",
                                  }}
                                />
                              </div>
                            ),
                            th: ({ node, ...props }) => (
                              <th {...props} className="bg-light" />
                            ),
                          }}
                        >
                          {processedAnswer}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>

                  {/* Documents Section */}
                  {message.documents && message.documents.length > 0 && (
                    <div className="documents-section mb-4 pt-3 border-top">
                      <h6 className="text-muted mb-3">Related Documents</h6>
                      <div className="d-flex flex-column gap-2">
                        {message.documents.map((doc, idx) => (
                          <div
                            key={doc}
                            className="d-flex align-items-center border rounded overflow-hidden document-card custom-followup-btn"
                            style={{
                              backgroundColor: "#f8f9fa",
                              transition: "all 0.2s ease-in-out",
                            }}
                          >
                            <div className="d-flex align-items-center justify-content-center text-white bg-purple index-box px-2 py-1">
                              {String(idx + 1).padStart(2, "0")}
                            </div>
                            <div className="flex-grow-1 px-3 py-2 small text-dark document-title">
                              {doc}
                            </div>
                            <div
                              className="d-flex align-items-center justify-content-center bg-light-purple show-cursor document-action"
                              style={{ padding: "8px 12px" }}
                              title="Open Document"
                              onClick={onOpenDocumentClick}
                            >
                              <img src={viewDocumentIcon} alt="Open" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Source Links Section */}
                  {message.sources && message.sources.length > 0 && (
                    <div className="source-links mt-4 pt-3 border-top">
                      <h6 className="text-muted mb-3">Related Resources</h6>
                      <div className="d-flex flex-column gap-2">
                        {message.sources.map((source, index) => (
                          <a
                            key={index}
                            href={source}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary text-start custom-followup-btn"
                            style={{
                              fontSize: "0.9rem",
                              padding: "0.5rem 1rem",
                              borderColor: "#e0e0e0",
                              backgroundColor: "#f8f9fa",
                              transition: "all 0.2s ease-in-out",
                              textDecoration: "none",
                            }}
                          >
                            {source.split("/").pop()?.replace(/-/g, " ")}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Follow-up Questions Section */}
                  {followUpQuestions.length > 0 && (
                    <div className="follow-up-questions mt-4 pt-3 border-top">
                      <h6 className="text-muted mb-3">
                        Suggested Follow-up Questions
                      </h6>
                      <div className="d-flex flex-column gap-2">
                        {followUpQuestions.map((question, qIndex) => (
                          <button
                            key={qIndex}
                            className="btn btn-outline-primary text-start custom-followup-btn"
                            onClick={() => {
                              onFollowUpQuestionClick?.(question);
                              // After click, we'll scroll to top
                              setTimeout(() => {
                                scrollToTop();
                              }, 50);
                            }}
                            style={{
                              fontSize: "0.9rem",
                              padding: "0.5rem 1rem",
                              borderColor: "#e0e0e0",
                              backgroundColor: "#f8f9fa",
                              transition: "all 0.2s ease-in-out",
                            }}
                          >
                            {question}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="d-flex align-items-center gap-3 mt-4 pt-3 border-top">
                    <button
                      className={`btn btn-link p-0 ${
                        bookmarkStates[index]
                          ? "text-primary fw-bold"
                          : "text-muted"
                      }`}
                      title="Bookmark"
                      onClick={() => handleBookmark(index)}
                      disabled={bookmarkLoading[index]}
                      style={{
                        opacity: bookmarkLoading[index] ? 0.5 : 1,
                        transform: bookmarkStates[index]
                          ? "scale(1.1)"
                          : "scale(1)",
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      {bookmarkLoading[index] ? (
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      ) : (
                        <img
                          src={saveIcon}
                          alt="Bookmark"
                          className="action-icon"
                          style={{
                            filter: bookmarkStates[index]
                              ? "brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(190deg) brightness(118%) contrast(119%)"
                              : "none",
                          }}
                        />
                      )}
                    </button>

                    <button
                      className={`btn btn-link p-0 ${
                        feedbackStates[index]?.like
                          ? "text-primary fw-bold"
                          : "text-muted"
                      }`}
                      title="Like"
                      onClick={() => handleFeedback(index, true)}
                      disabled={isSubmitting[index]}
                      style={{
                        opacity: isSubmitting[index] ? 0.3 : 1,
                        transform: feedbackStates[index]?.like
                          ? "scale(1.1)"
                          : "scale(1)",
                        transition: "all 0s ease-in-out",
                      }}
                    >
                      <img
                        src={likeIcon}
                        alt="Like"
                        className="action-icon"
                        style={{
                          filter: feedbackStates[index]?.like
                            ? "brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(190deg) brightness(118%) contrast(119%)"
                            : "none",
                        }}
                      />
                    </button>

                    <button
                      className={`btn btn-link p-0 ${
                        feedbackStates[index]?.dislike
                          ? "text-primary fw-bold"
                          : "text-muted"
                      }`}
                      title="Dislike"
                      onClick={() => handleFeedback(index, false)}
                      disabled={isSubmitting[index]}
                      style={{
                        opacity: isSubmitting[index] ? 0.3 : 1,
                        transform: feedbackStates[index]?.dislike
                          ? "scale(1.1)"
                          : "scale(1)",
                        transition: "all 0s ease-in-out",
                      }}
                    >
                      <img
                        src={dislikeIcon}
                        alt="Dislike"
                        className="action-icon"
                        style={{
                          filter: feedbackStates[index]?.dislike
                            ? "brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(190deg) brightness(118%) contrast(119%)"
                            : "none",
                        }}
                      />
                    </button>

                    <CopyButton text={message.answer} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}